import React, { useEffect, useState, useCallback } from "react";
import { useLang } from "@/context/LanguageContext";
import { api } from "@/lib/api";
import { PageShell, PageHero } from "@/components/layout/PageShell";
import { GRListItem } from "@/components/common/GRListItem";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Search } from "lucide-react";
import { GMOAA } from "@/constants/testIds";

const filters = [
  { v: "all", key: "filterAll" },
  { v: "ayush", key: "filterAyush" },
  { v: "health", key: "filterHealth" },
  { v: "gmoaa", key: "filterGmoaa" },
];

const GRNotices = () => {
  const { t, lang } = useLang();
  const [type, setType] = useState("gr,legal");
  const [category, setCategory] = useState("all");
  const [query, setQuery] = useState("");
  const [rows, setRows] = useState([]);

  const typeTabs = [
    { v: "gr,legal", en: "GR & Orders", gu: "ઠરાવ અને આદેશ" },
    { v: "notice", en: "Notifications", gu: "સૂચનાઓ" },
    { v: "circular,training", en: "Circulars & CME", gu: "પરિપત્ર અને તાલીમ" },
  ];

  const load = useCallback(() => {
    api.grs({ type, category, q: query || undefined }).then(setRows);
  }, [type, category, query]);

  useEffect(() => { load(); }, [load]);

  return (
    <PageShell>
      <PageHero title={t("navGr")} subtitle={t("grTitle")} />
      <div className="gmoaa-container py-10">
        <Tabs value={type} onValueChange={setType}>
          <TabsList className="bg-gmoaa-green-light">
            {typeTabs.map((tab) => (
              <TabsTrigger key={tab.v} data-testid={`gr-tab-${tab.v.split(",")[0]}`} value={tab.v} className="data-[state=active]:bg-gmoaa-green data-[state=active]:text-white">
                {lang === "gu" ? tab.gu : tab.en}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>

        <div className="mt-6 flex flex-col gap-4 sm:flex-row sm:items-center">
          <div className="relative flex-1">
            <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gmoaa-text-muted" />
            <Input
              data-testid={GMOAA.grSearch}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={t("searchGr")}
              className="border-gmoaa-border-subtle bg-white pl-10"
            />
          </div>
          <div className="flex flex-wrap gap-2">
            {filters.map((f) => (
              <button
                key={f.v}
                data-testid={`${GMOAA.grFilter}-${f.v}`}
                onClick={() => setCategory(f.v)}
                className={`rounded-full px-3 py-1.5 text-xs font-medium transition-colors ${
                  category === f.v ? "bg-gmoaa-green text-white" : "border border-gmoaa-border-subtle bg-white text-gmoaa-text-dark hover:bg-gmoaa-green-light"
                }`}
              >
                {t(f.key)}
              </button>
            ))}
          </div>
        </div>

        <div className="mt-6 space-y-3">
          {rows.map((g) => <GRListItem key={g.id} gr={g} />)}
          {rows.length === 0 && <p className="py-10 text-center text-gmoaa-text-muted">{t("noResults")}</p>}
        </div>
        <p className="mt-6 rounded-lg border border-gmoaa-border-subtle bg-gmoaa-green-light/40 px-4 py-3 text-xs text-gmoaa-text-muted" data-testid="gr-original-note">
          {t("checkOriginal")}
        </p>
      </div>
    </PageShell>
  );
};

export default GRNotices;
